<template>
  <div :class="['alert', metricName]">

    <div class="alert__title">
      {{ $t('amd:alerts_configuration_title', { metricName: name }) }}
    </div>

    <section class="row one alert__settings">

      <article class="alert__settings__type">

        <div class="alert__settings__type__label label">
          {{ $t('amd:alerts_configuration_type_label') }}
        </div>

        <le-select theme="theme-light"
                   class="alert__settings__type__combo"
                   type="dropdown"
                   :items="conditionTypesOne"
                   :placeholder="`${$t('amd:alerts_configuration_type_combo_placeholder')}`"
                   v-model="model.thresholdOne.condition"
                   @input="(value) => onConditionTypeChange(value, 'thresholdOne')"

        />

      </article>

      <article class="alert__settings__values">

        <div class="alert__settings__values__block">

          <div class="alert__settings__values__block__single">

            <div class="alert__settings__values__label label">
              {{ $t('amd:alerts_configuration_threshold_label') }}
            </div>

            <le-input-field ref="thresholdOne"
                            :placeholder="`${$t('amd:alerts_configuration_threshold_single_placeholder')}`"
                            class="alert__settings__values__block__single__value input"
                            :maxChars="setMaxLength('thresholdOne').maxChars"
                            :minValue="setMaxLength('thresholdOne').minValue"
                            :maxValue="setMaxLength('thresholdOne').maxValue"
                            v-model="model.thresholdOne.value"
                            @input="(value) => onSingleValueChange(value, 'thresholdOne')"
            />

          </div>

        </div>

      </article>

      <le-button class="alert__settings__reset"
                 variant="link"
                 :label="$t('amd:alerts_configuration_threshold_clear')"
                 @click="clearInput('thresholdOne')"
      />

    </section>

    <section v-if="showSecondCondition" class="row alert__settings">

      <article class="alert__settings__type">

        <div class="alert__settings__type__label label">
          {{ $t('amd:alerts_configuration_type_label') }}
        </div>

        <le-select theme="theme-light"
                   class="alert__settings__type__combo"
                   type="dropdown"
                   :items="[conditionTypesTwo]"
                   :placeholder="`${$t('amd:alerts_configuration_type_combo_placeholder')}`"
                   v-model="model.thresholdTwo.condition"
                   @input="(value) => onConditionTypeChange(value, 'thresholdTwo')"
        />

      </article>

      <article class="alert__settings__values">

        <div class="alert__settings__values__block">

          <div class="alert__settings__values__block__single">

            <div class="alert__settings__values__label label">
              {{ $t('amd:alerts_configuration_threshold_label') }}
            </div>

            <le-input-field ref="thresholdTwo"
                            :placeholder="`${$t('amd:alerts_configuration_threshold_single_placeholder')}`"
                            class="alert__settings__values__block__single__value input"
                            :maxChars="setMaxLength('thresholdTwo').maxChars"
                            :minValue="setMaxLength('thresholdTwo').minValue"
                            :maxValue="setMaxLength('thresholdTwo').maxValue"
                            v-model="model.thresholdTwo.value"
                            @input="(value) => onSingleValueChange(value, 'thresholdTwo')"
            />
          </div>

        </div>
      </article>

      <le-button class="alert__settings__reset"
                 variant="link"
                 :label="$t('amd:alerts_configuration_threshold_clear')"
                 @click="clearInput('thresholdTwo')"
      />

      <general-icon class="remove"
                    icon="close-icon"
                    tabindex="0"
                    role="button"
                    :aria-label="$t('amd:alerts_configuration_threshold_remove')"
                    @keydown.space.enter.prevent="removeRow"
                    @click.native="removeRow"
      />

    </section>

    <le-button v-if="!showSecondCondition"
               class="add"
               variant="link"
               :label="$t('amd:alerts_configuration_add')"
               :aria-label="$t('amd:alerts_configuration_add')"
               @click="addRow"
    />

  </div>
</template>

<script>
/* eslint-disable max-len, no-unused-expressions */
import { map, isUndefined, isNaN, each, omit } from 'lodash';
import { ALERT_CONDITION_TYPES } from '@config/Consts';

const ALERT_CONDITIONS = omit(ALERT_CONDITION_TYPES, ['BETWEEN']);

const initialState = () => ({
  thresholdOne: {
    condition: { id: 'below', value: 'below' },
    value: '',
  },
  thresholdTwo: {
    condition: null,
    value: '',
  },
});

export default {
  name: 'alert-numeric-with-add',
  props: {
    metricName: {
      type: String,
      required: true,
    },
    conditionType: {
      type: String,
      default: undefined,
    },
    values: {
      type: Object,
      default: undefined,
    },
    // validation: {
    //   type: Object,
    //   default: undefined,
    // },
    name: {
      type: String,
      default: '',
    },
    validation: {
      type: Object,
    },
  },
  data() {
    return {
      changeEventEmitted: false,
      showSecondCondition: false,
      conditionTypesOne: map(ALERT_CONDITIONS, item => ({ id: item, value: item })),
      model: initialState(),
      validation: this.validation,
    };
  },
  computed: {
    // here we check what is selected at row one and set the value to an opposite below -> above etc.
    // this computed is used as le-select items props as well as a v-model for second row
    conditionTypesTwo() {
      if (this.model.thresholdOne.condition.value === 'below') {
        return { id: 'above', value: 'above' };
      }
      return { id: 'below', value: 'below' };
    },
  },
  methods: {
    addRow() {
      this.showSecondCondition = true;
      this.model.thresholdTwo.condition = this.conditionTypesTwo;
    },
    removeRow() {
      this.showSecondCondition = false;
      this.model.thresholdTwo.condition = null;
      this.model.thresholdTwo.value = '';
      this.emitChanges('removedRow', 'thresholdTwo');
    },
    setErrorInControls(showError, message, controls) {
      each(controls, control => control && control.setError(showError, message));
    },
    validateNumeric(value) {
      return !(!value || isNaN(Number(value)));
    },
    validateControl(model, control) {
      const isBelowType = model.condition.value === 'below';
      const { below, above } = this.validation;
      if (!this.validateNumeric(model.value)) {
        this.setErrorInControls(
          true,
          this.setErrorMessage(isBelowType ? below : above),
          [control],
        );
        return false;
      }
      if (isBelowType) {
        const isValid = !(model.value < below.from || model.value > below.to);
        return !isValid ? this.setErrorInControls(
          true,
          this.setErrorMessage(below),
          [control],
        ) || false : true;
      }
      const isValid = !(model.value < above.from || model.value > above.to);
      return !isValid ? this.setErrorInControls(
        true,
        this.setErrorMessage(above),
        [control],
      ) || false : true;
    },
    validateControls() {
      if (!this.showSecondCondition) {
        return this.validateControl(this.model.thresholdOne, this.$refs.thresholdOne);
      }
      return this.validateControl(this.model.thresholdOne, this.$refs.thresholdOne) &&
        this.validateControl(this.model.thresholdTwo, this.$refs.thresholdTwo);
    },
    formatForDropdown(value) {
      return { id: value, value };
    },
    clearSelectedValues() {
      this.model = initialState();
    },
    clearValidationMessageFields() {
      const inputs = [this.$refs.thresholdOne, this.$refs.thresholdTwo];
      inputs.forEach(input => input && !isUndefined(input) && input.clearError());
    },
    emitChanges(field, value) {
      if (!this.changeEventEmitted) {
        this.$emit('onChange', { metricName: this.metricName, field, value });
        this.changeEventEmitted = true;
      }
    },
    onConditionTypeChange(conditionType, row) {
      if (row === 'thresholdOne') {
        this.model.thresholdTwo.condition = this.conditionTypesTwo;
      }
      this.emitChanges('conditionType', conditionType.value);
    },
    onSingleValueChange(value, row) {
      this.emitChanges('singleValue', { value, row });
    },
    clearInput(row) {
      this.model[row].value = '';
      this.$refs[row].clearError();
      this.emitChanges('clearedInput', row);
    },
    setErrorMessage({ from, to }) {
      return this.$t('amd:alerts_configuration_threshold_range_validation', { from, to });
    },
    setMaxLength(row) {
      const { below, above } = this.validation;
      if (this.model[row].condition.value === 'below') {
        return {
          maxChars: below.maxChars,
          maxValue: below.to,
          minValue: below.from,
        };
      }
      return {
        maxChars: above.maxChars,
        maxValue: above.to,
        minValue: above.from,
      };
    },

    /* *** PUBLIC METHODS ***** */
    validate() {
      const isValid = this.validateControls();
      if (isValid) {
        this.clearValidationMessageFields();
      }
      const inputData = isValid ? {} : {
        metricName: this.metricName,
        conditionType: this.metricName,
      };
      return { ...inputData, isValid };
    },
    reset() {
      this.$emit('onClear', this.getAlertData());
      this.clearSelectedValues();
      this.clearValidationMessageFields();
    },
    getAlertData() {
      const result = {
        metricName: this.metricName,
      };
      if (this.model.thresholdOne.value) {
        result.conditionType = this.metricName;
        result.values = {};
        result.values.thresholdOne = {
          condition: this.model.thresholdOne.condition.value,
          value: Number(this.model.thresholdOne.value),
        };
        // result.values.thresholdOne = this.model.thresholdOne.value;
      }
      if (this.showSecondCondition && this.model.thresholdTwo.value && this.model.thresholdTwo.condition) {
        result.values.thresholdTwo = {
          condition: this.model.thresholdTwo.condition.value,
          value: Number(this.model.thresholdTwo.value),
        };
      }
      return result;
    },
  },
  watch: {
    values: {
      handler() {
        if (this.values) { // values { one: { condition, value }}
          if (this.values.thresholdOne) {
            this.model.thresholdOne.condition = this.formatForDropdown(this.values.thresholdOne.condition);
            this.model.thresholdOne.value = this.values.thresholdOne.value;
          }
          if (this.values.thresholdTwo) {
            this.model.thresholdTwo.condition = this.formatForDropdown(this.values.thresholdTwo.condition);
            this.model.thresholdTwo.value = this.values.thresholdTwo.value;
            this.showSecondCondition = true;
          }
        }
      },
      immediate: true,
    },
    selectedConditionType() {
      this.clearSelectedValues();
      this.clearValidationMessageFields();
    },
  },
};
</script>

<style lang="scss" scoped>
.alert {
  margin: 5px 0 10px 0;

  .input {
    width: 85px;
  }

  .label {
    @include alert-dialog-label();
  }

  &__title {
    font-weight: bold;
    margin-bottom: 10px;
  }

  &__settings {
    display: flex;
    align-items: center;

    &__type {
      &__combo {
        width: 168px;
      }
    }

    &__values {
      margin-left: 10px;

      &__block {
        &__range {
          display: flex;
          align-items: center;

          &__separator {
            width: 5px;
            height: 1px;
            margin: 16px 10px 0;
            @include themify {
              background-color: getColor('mainFilterSeparator', 'amd');
            }
          }
        }
      }
    }

    &__reset {
      margin: 18px 0 0 10px;
    }
  }

  .row {
    margin-bottom: 10px;

    .remove {
      margin: 20px 0 0 15px;
    }
  }

}
</style>
